import React, {useEffect, useRef, useState} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {H6} from '@modifi/ui'

import {FlippableCardContainer, FlipCard, CardFront, CardBack} from './styles'

const propTypes = {
  cardDetails: PropTypes.shape({
    image: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    description: PropTypes.string,
  }),
}

const defaultProps = {
  cardDetails: {
    image: '',
    text: '',
    description: '',
  },
}

export type FlippableCardProps = InferPropTypes<typeof propTypes, typeof defaultProps>

export const FlippableCard = React.forwardRef<HTMLDivElement, FlippableCardProps>(
  ({cardDetails}, ref) => {
    const [isCardFlipped, setIsCardFlipped] = useState(true)
    let timeoutId: null | ReturnType<typeof setTimeout> = null
    const targetRef = useRef<HTMLDivElement>(null)

    const flipTheCard = () => {
      if (timeoutId) clearTimeout(timeoutId)
      setIsCardFlipped(true)
    }

    const unflipTheCard = () => {
      timeoutId = setTimeout(() => setIsCardFlipped(false), 2000)
    }

    useEffect(() => {
      const targetElement = targetRef.current
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && targetElement) {
            setTimeout(() => setIsCardFlipped(false), 1000)
            observer.unobserve(targetElement)
          }
        },
        {
          threshold: 0.5,
        }
      )

      if (targetElement) {
        observer.observe(targetElement)
      }

      return () => {
        if (targetElement) {
          observer.unobserve(targetElement)
        }
      }
    }, [])

    return (
      <FlippableCardContainer ref={ref}>
        <FlipCard
          onMouseEnter={flipTheCard}
          onMouseLeave={unflipTheCard}
          isCardFlipped={isCardFlipped}
          data-testid={isCardFlipped ? 'card-back' : 'card-front'}
          ref={targetRef}
        >
          <CardFront>
            <img src={cardDetails?.image} alt={`flip-card-${cardDetails?.text}`} />
            <H6 mx={16} textAlign="center">
              {cardDetails?.text}
            </H6>
          </CardFront>
          <CardBack>{cardDetails?.description}</CardBack>
        </FlipCard>
      </FlippableCardContainer>
    )
  }
)

FlippableCard.propTypes = propTypes

FlippableCard.defaultProps = defaultProps

export default FlippableCard
